import { susDpiBasculeCodes, susDpiBasculeLabelKeys } from '@/models/sus.type';
import { Select, SimpleGrid, Stack, Switch, Textarea, TextInput } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import { Controller, useWatch, type Control } from 'react-hook-form';
import SusRequiredLabel from './SusRequiredLabel';
import type { SusFormValues } from './susFormSchema';

type Props = {
  control: Control<SusFormValues>;
  disabled: boolean;
};

/** Booléen facultatif ↔ valeur du `Select` Oui / Non (vide = non renseigné). */
const toBooleanSelectValue = (value: boolean | null): string | null =>
  value == null ? null : String(value);

/** Saisie des métadonnées DPI (`metadonnees`) d'un service d'urgence. */
const SusDpiFields = ({ control, disabled }: Props) => {
  const { t } = useTranslate();
  const dpiBascule = useWatch({ control, name: 'metadonnees.dpi_bascule' });

  const dpiBasculeOptions = susDpiBasculeCodes.map(code => ({
    value: code,
    label: t(susDpiBasculeLabelKeys[code]),
  }));
  const booleanOptions = [
    { value: 'true', label: t('admin.sus.transmission.oui') },
    { value: 'false', label: t('admin.sus.transmission.non') },
  ];

  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <Controller
          control={control}
          name="metadonnees.logiciel"
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label={t('admin.sus.detail.metadonnees.logiciel')}
              description={t('admin.sus.form.metadonnees.logiciel.description')}
              maxLength={50}
              error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
              disabled={disabled}
            />
          )}
        />
        <Controller
          control={control}
          name="metadonnees.dpi_version"
          render={({ field }) => (
            <TextInput
              {...field}
              label={t('admin.sus.detail.metadonnees.dpi_version')}
              disabled={disabled}
            />
          )}
        />
      </SimpleGrid>
      <Controller
        control={control}
        name="metadonnees.dpi_bascule"
        render={({ field }) => (
          <Select
            ref={field.ref}
            name={field.name}
            onBlur={field.onBlur}
            value={field.value ?? null}
            onChange={value => field.onChange(value)}
            data={dpiBasculeOptions}
            label={t('admin.sus.detail.metadonnees.dpi_bascule')}
            clearable
            disabled={disabled}
          />
        )}
      />
      {/* Masqué si « Non » : la valeur saisie reste dans le formulaire et est conservée. */}
      {dpiBascule !== 'Non' && (
        <Controller
          control={control}
          name="metadonnees.dpi_bascule_precisions"
          render={({ field }) => (
            <Textarea
              {...field}
              label={t('admin.sus.detail.metadonnees.dpi_bascule_precisions')}
              autosize
              minRows={2}
              disabled={disabled}
            />
          )}
        />
      )}
      <Controller
        control={control}
        name="metadonnees.transmission_rpu_clos"
        render={({ field }) => (
          <Select
            ref={field.ref}
            name={field.name}
            onBlur={field.onBlur}
            value={toBooleanSelectValue(field.value)}
            onChange={value => field.onChange(value == null ? null : value === 'true')}
            data={booleanOptions}
            label={t('admin.sus.detail.metadonnees.transmission_rpu_clos')}
            description={t('admin.sus.form.metadonnees.transmission_rpu_clos.description')}
            clearable
            disabled={disabled}
          />
        )}
      />
      <Controller
        control={control}
        name="metadonnees.supra_etab"
        render={({ field }) => (
          <Switch
            ref={field.ref}
            name={field.name}
            onBlur={field.onBlur}
            checked={field.value}
            onChange={event => field.onChange(event.currentTarget.checked)}
            label={<SusRequiredLabel label={t('admin.sus.detail.metadonnees.supra_etab')} />}
            description={t('admin.sus.form.metadonnees.supra_etab.description')}
            disabled={disabled}
            mt="xs"
          />
        )}
      />
    </Stack>
  );
};

export default SusDpiFields;
